import {
    Component,
    OnDestroy,
    ChangeDetectionStrategy,
    Input,
    ViewChild,
    ViewEncapsulation,
    ElementRef
} from "@angular/core";
import {AppService} from "@irb/app.service";
import {
    IEvent,
    IEventSubscriber,
    IrbClientsEvents,
    IrbModelsEvents,
    IrbEditorsEvents,
    IrbAppEvents,
    IrbRebalanceEvents
} from "@irb-common/events";
import {ReplaySubject, Subscription} from "rxjs";
import { IrbOrdersEvents } from "@irb/irb-orders/IrbOrdersEvents";

@Component({ standalone: false,
    selector: "irb-editor-sidebar",
    templateUrl: "./editor-sidebar.component.html",
    styleUrls: ["./editor-sidebar.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.None
})
export class EditorSidebarComponent implements OnDestroy, IEventSubscriber {
    _slot: string;
    fullscreen: boolean;
    @Input() isModal: boolean = false;
    @ViewChild("overlayComponent") private overlay: any;

    private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
    _hasUnsavedChanges: boolean = false;
    isSideBarVisible: boolean = false;
    template: string;
    eventSubscription: Subscription;
    bodyHeight: string;
    onClose: Function;


    constructor(private as: AppService) {
        this.eventSubscription = this.as.getEvents().subscribe((s) => {
            this.processEvent(s);
        });
    }

    ngOnDestroy() {
        this.eventSubscription.unsubscribe();
        this.destroyed$.next(true);
        this.destroyed$.complete();
    }

    processEvent(event: IEvent) {
        const targetSlot: string = (event.data) ? event.data.slot || "A" : "A";

        if (targetSlot !== this.slot) {
            return;
        }

        switch (event.type) {
            case IrbOrdersEvents.SHOW_QUICK_ORDER_ENTRY:
                this.fireLoadEvent("quickOrderEntry", event.data);
                break;
            case IrbClientsEvents.SHOW_EDIT_TRADING_INSTRUCTIONS:
                this.fireLoadEvent("editTradingInstructions", event.data);
                break;
            case IrbClientsEvents.SHOW_EDIT_PORTFOLIO:
                this.fireLoadEvent("portfolio", event.data);
                break;
            case IrbClientsEvents.SHOW_EDIT_ACCOUNT:
                this.fireLoadEvent("account", event.data);
                break;
            case IrbModelsEvents.SHOW_EDIT_MODEL:
                const template = event.data.type === 'Base' ? 'model' : event.data.type === 'Blended' ? 'blendedModel' : undefined;
                this.fireLoadEvent(template, event.data);
                break;
            case IrbModelsEvents.SHOW_EDIT_SECURITY:
                this.fireLoadEvent("editSecurity", event.data);
                break;
            case IrbModelsEvents.SHOW_EDIT_MULTIPLE_SECURITIES:
                this.fireLoadEvent("editMultipleSecurity", event.data);
                break;
            case IrbClientsEvents.SHOW_EDIT_MULTIPLE_PORTFOLIOS:
                this.fireLoadEvent("multiplePortfolios", event.data);
                break;
            case IrbClientsEvents.SHOW_EDIT_MULTIPLE_ACCOUNTS:
                this.fireLoadEvent("editmultipleaccounts", event.data);
                break;
            case IrbRebalanceEvents.SHOW_EDIT_CREATE_REBALANCE:
                this.fireLoadEvent("fullRebalance", event.data);
                break;
            case IrbRebalanceEvents.SHOW_GENERATE_INVEST_CASH_REBALANCE:
                this.fireLoadEvent("generateinvestcashRebalance", event.data);
                break;
            case IrbRebalanceEvents.SHOW_INVEST_CASH_REBALANCE:
                this.fireLoadEvent("investcashRebalance", event.data);
                break;
            case IrbRebalanceEvents.SHOW_GENERATE_CASH_REBALANCE:
                this.fireLoadEvent("generatecashRebalance", event.data);
                break;
            case IrbRebalanceEvents.MANUAL_ADJUSTMENT_REBALANCE:
                this.fireLoadEvent("manualadjustmentRebalance", event.data);
                break;
            case IrbRebalanceEvents.SHOW_ADJUST_SECURITIES_REBALANCE:
                this.fireLoadEvent("AdjustSecuriritesRebalance", event.data);
                break;
            case IrbRebalanceEvents.SHOW_REPLACE_SECURITIES_REBALANCE:
                this.fireLoadEvent("ReplaceSecuritiesRebalance", event.data);
                break;
            case IrbRebalanceEvents.SHOW_HARVEST_TAX_LOSS_REBALANCE:
                this.fireLoadEvent("HarvestTaxLossRebalance", event.data);
                break;
            case IrbRebalanceEvents.SHOW_PORTFOLIO_RESULT_DETAIL:
                this.fireLoadEvent("portfolioResultDetail", event.data);
                break;
            case IrbEditorsEvents.IS_MODIFIED:
                this.hasUnsavedChanges = true;
                break;
            case IrbEditorsEvents.IS_NOT_MODIFIED:
                this.hasUnsavedChanges = false;
                break;
            case IrbEditorsEvents.SAVE_SUCCESS:
                this.hasUnsavedChanges = false;
                this.overlay.hideWait();

                if (event.data && event.data.data) {
                    this.showMessage(event.data.data, "success", event.data.summary);
                }

                if (event.data && event.data.removeOnClose) {
                    this.onClose = null;
                }

                if (event.data && event.data.hasPendingFunction) {
                    this.as.fireEvent(IrbEditorsEvents.CALL_PENDING_FUNCTION, event.data);
                }

                break;
            case IrbEditorsEvents.WARN_SUCCESS:
                this.hasUnsavedChanges = false;
                this.overlay.hideWait();

                if (event.data && event.data.data) {
                    this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
                        severity: "warn",
                        summary: event.data.summary ? event.data.summary : "Warn",
                        detail: event.data.data
                    });
                }

                if (event.data && event.data.removeOnClose) {
                    this.onClose = null;
                }

                if (event.data && event.data.hasPendingFunction) {
                    this.as.fireEvent(IrbEditorsEvents.CALL_PENDING_FUNCTION, event.data);
                }
                break;
            case IrbEditorsEvents.SAVE_ERROR:
                this.overlay.hideWait();
                let msg = "An error occurred processing your request.";
                if (event.data && event.data.msg) {
                    msg = event.data.msg;
                }
                this.showMessage(msg, "error");
                break;
            case IrbEditorsEvents.CLOSE_WITHOUT_SAVE:
                if(this.isSideBarVisible) {
                    this.hasUnsavedChanges = false;

                    if (event.data && event.data.hasPendingFunction) {
                        this.as.fireEvent(IrbEditorsEvents.CALL_PENDING_FUNCTION, event.data);
                    } else {
                        this.callOnClose();
                        this.as.fireEvent(IrbEditorsEvents.CLOSE, event.data);
                        this.template = "empty";
                        this.isSideBarVisible = false;
                    }
                }
                break;
            case IrbEditorsEvents.SAVE_START:
                this.overlay.showWait();
                break;
            case IrbEditorsEvents.HIDE_WAIT:
                this.overlay.hideWait();
                break;
            case IrbRebalanceEvents.SHOW_EDIT_COVEREDCALL_REBALANCE:
                this.fireLoadEvent("coveredcallRebalance", event.data);
                break;
            case IrbModelsEvents.SHOW_EDIT_MMC_MODEL:
                this.fireLoadEvent("editMMCModel", event.data);
                break;
            case IrbModelsEvents.SHOW_EDIT_MMC_ADVISOR_MODEL:
                this.fireLoadEvent("editMMCAdvisorModel", event.data);
                break;
            case IrbRebalanceEvents.SHOW_TLH_FULL_REBALANCE:
                this.fireLoadEvent("TlhFullRebalance", event.data);
                break;
        }
    }

    fireLoadEvent(template: string, data: any) {
        const ignoreUnsavedChanges = (data) ? data.ignoreUnsavedChanges || false : false;

        if (!this.hasUnsavedChanges || ignoreUnsavedChanges) {
            this.template = template;
            this.fullscreen = (data) ? data.fullscreen || false : false;
            this.isModal = (data) ? data.isModal || false : false;
            this.onClose = (data) ? data.onClose || null : null;
            data.slot = this.slot;
            this.isSideBarVisible = true;
            window.setTimeout(() => {
                this.as.fireEvent(IrbEditorsEvents.LOAD, data);
            });
        } else {
            this.as.fireEvent(IrbEditorsEvents.UNSAVED_CHANGES, {
                "pendingFunction": (() => {
                    this.fireLoadEvent(template, data);
                }).bind(this),
                "slot": this.slot
            });
        }
    }

    save() {
        this.as.fireEvent(IrbEditorsEvents.SAVE, {"slot": this.slot});
    }

    showMessage(msg: string, severity: string, summary?: string) {
        this.as.fireEvent(IrbAppEvents.SHOW_TOAST, {
            severity: severity,
            summary: summary ? summary : "Save",
            detail: msg
        });
    }

    close() {
        if (this.hasUnsavedChanges) {
            this.as.fireEvent(IrbEditorsEvents.UNSAVED_CHANGES, {"slot": this.slot});
        } else {
            this.callOnClose();
            this.as.fireEvent(IrbEditorsEvents.CLOSE, {"slot": this.slot});
            this.template = "empty";
            this.isSideBarVisible = false;
        }
    }

    callOnClose() {
        if (this.onClose) {
            this.onClose();
            this.onClose = null;
        }
    }

    public get hasUnsavedChanges(): boolean {
        return this._hasUnsavedChanges;
    }

    public set hasUnsavedChanges(value: boolean) {
        this._hasUnsavedChanges = value;
    }

    @Input() get slot() {
        return this._slot;
    }

    set slot(slot: string) {
        this._slot = slot;
    }
}
